import React, { useState, useEffect, useCallback } from "react";
import FaiTypography from "../../../../../../MuiComponents/FaiTypography";
import theme from "../../../../../../assets/theme";
import FaiBox from "../../../../../../MuiComponents/FaiBox";
import { Grid, Icon, Checkbox, FormControlLabel } from "@mui/material";
import FormField from "../../../../../../components/FormField";
import FaiSelect from "../../../../../../MuiComponents/FaiSelect";
import FaiInput from "../../../../../../MuiComponents/FaiInput";
import FaiDropzone from "../../../../../../MuiComponents/FaiDropzone";
import { ProductController } from "../../../../../../controllers/productController";
import {
	MasterCategory,
	ProductType,
} from "../../../../../../types/productTypes";
import { merchantAuthController } from "../../../../../../controllers/authController";
import { DropzoneArea } from "react-mui-dropzone";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

interface DetailFormProps {
	formData: any;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
}

interface SelectOption {
	value: string;
	label: string;
}

function DetailForm({ formData, setFormData }: DetailFormProps) {
	const { typography, palette } = theme;
	const [masterCategories, setMasterCategories] = useState<MasterCategory[]>(
		[]
	);
	const [productTypes, setProductTypes] = useState<ProductType[]>([]);
	const [selectedProductType, setSelectedProductType] =
		useState<ProductType | null>(null);
	const [loading, setLoading] = useState({
		categories: false,
		productTypes: false,
		productTypeMetadata: false,
		brand: false,
	});
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
	const [metadataInputValues, setMetadataInputValues] = useState<
		Record<string, string>
	>({});
	const [media, setMedia] = useState({
		sizeChartMedia: null,
	});
	const [isEditMode, setIsEditMode] = useState<boolean>(false);

	useEffect(() => {
		fetchMasterCategories();
		fetchBrands();
	}, []);

	// Detect edit mode and ensure proper initialization
	useEffect(() => {
		if (formData.masterCategoryId || formData.categoryId) {
			setIsEditMode(true);
			console.log(
				"Edit mode detected with masterCategoryId:",
				formData.masterCategoryId,
				"categoryId:",
				formData.categoryId
			);
			setMedia((prev) => ({
				...prev,
				sizeChartMedia: formData.sizeChartMedia,
			}));
		}
	}, [formData.masterCategoryId, formData.categoryId]);

	useEffect(() => {
		if (selectedCategory) {
			fetchProductTypes(selectedCategory);
		}
	}, [selectedCategory]);

	// Initialize selected category when formData has masterCategoryId
	useEffect(() => {
		if (formData.masterCategoryId && !selectedCategory) {
			console.log(
				"Setting selected category from formData:",
				formData.masterCategoryId
			);
			setSelectedCategory(formData.masterCategoryId);
		}
	}, [formData.masterCategoryId, selectedCategory]);

	// Ensure selected category is set when master categories are loaded
	useEffect(() => {
		if (
			masterCategories.length > 0 &&
			formData.masterCategoryId &&
			!selectedCategory
		) {
			console.log(
				"Setting selected category after master categories loaded:",
				formData.masterCategoryId
			);
			setSelectedCategory(formData.masterCategoryId);
		}
	}, [masterCategories, formData.masterCategoryId, selectedCategory]);

	// In edit mode, ensure selected category is set when master categories are loaded
	useEffect(() => {
		if (
			isEditMode &&
			masterCategories.length > 0 &&
			formData.masterCategoryId &&
			!selectedCategory
		) {
			console.log(
				"Edit mode: Setting selected category after master categories loaded:",
				formData.masterCategoryId
			);
			setSelectedCategory(formData.masterCategoryId);
		}
	}, [
		isEditMode,
		masterCategories,
		formData.masterCategoryId,
		selectedCategory,
	]);

	// Initialize product type metadata when formData has productTypeId
	useEffect(() => {
		if (
			// (formData.productTypeId || formData.categoryId) &&
			formData.categoryId &&
			!selectedProductType
		) {
			console.log(
				"Fetching product type metadata for (formData.categoryId):",
				formData.categoryId
			);
			fetchProductTypeMetadata(formData.categoryId);
		}
		// }, [formData.productTypeId, formData.categoryId, selectedProductType]);
	}, [formData.categoryId, selectedProductType]);

	// Initialize product types when we have a master category and a sub-category ID
	useEffect(() => {
		if (selectedCategory && formData.categoryId && productTypes.length === 0) {
			console.log(
				"Fetching product types for master category:",
				selectedCategory,
				"with existing categoryId:",
				formData.categoryId
			);
			fetchProductTypes(selectedCategory);
		}
	}, [selectedCategory, formData.categoryId, productTypes.length]);

	// Ensure product types are fetched when master category is selected and we have a categoryId
	useEffect(() => {
		if (selectedCategory && formData.categoryId && productTypes.length === 0) {
			console.log(
				"Fetching product types after master category selection:",
				selectedCategory
			);
			fetchProductTypes(selectedCategory);
		}
	}, [selectedCategory, formData.categoryId, productTypes.length]);

	// In edit mode, ensure product types are fetched when we have both masterCategoryId and categoryId
	useEffect(() => {
		if (
			isEditMode &&
			formData.masterCategoryId &&
			formData.categoryId &&
			productTypes.length === 0
		) {
			console.log(
				"Edit mode: Fetching product types for master category:",
				formData.masterCategoryId
			);
			fetchProductTypes(formData.masterCategoryId);
		}
	}, [
		isEditMode,
		formData.masterCategoryId,
		formData.categoryId,
		productTypes.length,
	]);

	const fetchMasterCategories = async () => {
		setLoading((prev) => ({ ...prev, categories: true }));
		try {
			const response = await ProductController.getMasterCategories();
			if (
				response.data &&
				response.data.data &&
				response.data.data.masterCategories
			) {
				setMasterCategories(response.data.data.masterCategories);
			}
		} catch (error) {
			console.error("Error fetching master categories:", error);
		} finally {
			setLoading((prev) => ({ ...prev, categories: false }));
		}
	};

	const fetchProductTypes = async (categoryId: string) => {
		setLoading((prev) => ({ ...prev, productTypes: true }));
		try {
			const response = await ProductController.getCategoryByMastercategoryId(
				categoryId
			);
			// console.log("Product types response:", response);
			if (
				response.data &&
				response.data.data &&
				response.data.data.categories
			) {
				// console.log("Setting product types:", response.data.data.categories);
				setProductTypes(response.data.data.categories);
			}
		} catch (error) {
			console.error("Error fetching product types:", error);
		} finally {
			setLoading((prev) => ({ ...prev, productTypes: false }));
		}
	};

	const fetchProductTypeMetadata = async (productTypeId: string) => {
		setLoading((prev) => ({ ...prev, productTypeMetadata: true }));
		try {
			const response = await ProductController.getProductTypeMetadata(
				productTypeId
			);
			if (response.data && response.data.data) {
				setSelectedProductType(response.data.data);
				// Initialize metadata fields in formData, preserving existing values
				// and only adding missing fields with defaults
				setFormData((prev) => {
					const existingMetadata = prev.metadata || {};
					const schemaMetadata = response.data.data.metadata || {};

					// Merge: for each field in schema, use existing value if present, otherwise default
					const mergedMetadata: Record<string, any> = {};
					Object.entries(schemaMetadata).forEach(([key, meta]: [string, any]) => {
						if (existingMetadata.hasOwnProperty(key) && existingMetadata[key] !== undefined && existingMetadata[key] !== "") {
							// Preserve existing value
							mergedMetadata[key] = existingMetadata[key];
						} else {
							// Use default
							mergedMetadata[key] = meta.allowMultiple ? [] : "";
						}
					});

					return {
						...prev,
						productTypeId: response.data.data.id,
						metadata: mergedMetadata,
					};
				});
			}
		} catch (error) {
			console.error("Error fetching product type metadata:", error);
		} finally {
			setLoading((prev) => ({ ...prev, productTypeMetadata: false }));
		}
	};

	const fetchBrands = async () => {
		setLoading((prev) => ({ ...prev, brand: true }));
		try {
			const apiResponse: any = await merchantAuthController.getBrandList();
			// console.log("Brand List:", apiResponse.data.data.data);
			const brandOptions = apiResponse.data.data.data.map((category: any) => ({
				value: category.brandName,
				label: category.brandName,
			}));
			setBrandOptions(brandOptions);
		} catch (error) {
			console.error("Error fetching brands:", error);
		} finally {
			setLoading((prev) => ({ ...prev, brand: false }));
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev: any) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleMetadataChange = (key: string, value: string | string[]) => {
		setFormData((prev: any) => ({
			...prev,
			metadata: {
				...prev.metadata,
				[key]: value,
			},
		}));
	};

	const getOptionValue = (option: any): string => {
		if (option && typeof option === "object" && "value" in option) {
			return option.value as string;
		}
		return "";
	};

	const handleCategoryChange = (option: any) => {
		const value = getOptionValue(option);
		if (value) {
			setSelectedCategory(value);
			// Accessories have no size chart — drop any attached one so it isn't
			// submitted after the field is hidden.
			const newMaster = masterCategories.find((m) => m.id === value);
			const nowAccessory = /accessor/i.test(newMaster?.name || "");
			setFormData((prev: any) => ({
				...prev,
				masterCategoryId: value,
				// Only clear categoryId if we're not editing an existing product
				...(prev.categoryId ? {} : { categoryId: "" }),
				productTypeId: "",
				metadata: {},
				...(nowAccessory ? { sizeChartMedia: null } : {}),
			}));
			setSelectedProductType(null);
		}
	};

	const handleProductTypeChange = (option: any) => {
		const value = getOptionValue(option);
		if (typeof value === "string")
			if (value) {
				setFormData((prev: any) => ({
					...prev,
					categoryId: value,
				}));
				fetchProductTypeMetadata(value);
			}
	};

	const handleBrandChange = (option: any) => {
		const value = getOptionValue(option);
		if (value) {
			setFormData((prev: any) => ({
				...prev,
				brandName: value,
				brand: value,
			}));
		}
	};

	const handleReturnableChange = (option: any) => {
		const value = getOptionValue(option);
		setFormData((prev: any) => ({
			...prev,
			isReturnable: value === "true",
		}));
	};

	// Helper function to get the selected option object
	const getSelectedOption = (id: any, options: any[]) => {
		if (!id) return null;
		const found = options.find(
			(option) => option.value === id?.value || option.value === id
		);
		if (found) return found;

		// If not found, check if we're in edit mode and options are still loading
		if (isEditMode && options.length === 0) {
			// We're in edit mode and options haven't loaded yet
			return { value: id, label: `Loading...` };
		}
		if (typeof id !== "string") return { value: "", label: "Please select" };
		// If options are loaded but not found, show the ID
		return { value: id, label: id };
	};

	const getMetadataOptions = (key, meta) => {
		const metadataValue = formData.metadata?.[key];
		const basicValues = meta.values.map((val) => ({
			value: val,
			label: val,
		}));
		if (metadataValue?.isOther) {
			return [
				...basicValues,
				{ label: metadataValue.value, value: metadataValue.value },
			];
		}
		return basicValues;
	};

	const getMetadataValue = (key, meta) => {
		const selected = formData.metadata?.[key];

		if (!!meta.allowMultiple) {
			if (Array.isArray(selected)) {
				return selected.map((val) => {
					if (typeof val === "string") {
						return { value: val, label: val };
					} else if (val && typeof val === "object" && "value" in val) {
						return { value: val.value, label: val.value };
					}
					return { value: "", label: "" };
				});
			} else if (Array.isArray(selected?.value)) {
				return selected.value.map((val) => {
					if (typeof val === "string") {
						return { value: val, label: val };
					} else if (val && typeof val === "object" && "value" in val) {
						return { value: val.value, label: val.value };
					}
					return { value: "", label: "" };
				});
			}
			return [];
		} else {
			if (selected?.isOther) {
				return { label: selected.value, value: selected.value };
			}
			return getSelectedOption(
				formData.metadata?.[key] || "",
				getMetadataOptions(key, meta)
			);
		}
	};

	// Helper function to render metadata fields
	const renderMetadataFields = () => {
		if (!selectedProductType || !selectedProductType.metadata) return null;

		return (
			<FaiBox mt={3}>
				<FaiTypography
					variant={"h5"}
					fontSize={typography.size.lg}
					fontWeight={typography.fontWeightMedium}
				>
					Product Type Specifications
				</FaiTypography>
				<Grid container spacing={2} mt={1}>
					{Object.entries(selectedProductType.metadata).map(
						([key, meta]: any) => {
							const options = getMetadataOptions(key, meta);
							// Check if the key starts with "numberof" (case-insensitive)
							const isNumberOfField = key.toLowerCase().startsWith("numberof");

							return (
								<Grid item xs={12} sm={6} key={key}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												{key} {meta.isRequired && "*"}
											</FaiTypography>
										</FaiBox>
										{isNumberOfField ? (
											// Render text input for "numberof" fields
											<FormField
												label=""
												name={key}
												type="number"
												placeholder={`Enter ${key}`}
												value={formData.metadata?.[key] || ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													handleMetadataChange(key, e.target.value);
												}}
											/>
										) : (
											// Render dropdown for other fields
											<FaiSelect
												placeholder={`Select ${key}`}
												name={key}
												options={options}
												isMulti={!!meta.allowMultiple}
												onChange={(option) => {
													if (!!meta.allowMultiple) {
														// Multi-select: option is array
														const values = Array.isArray(option)
															? option.map(getOptionValue)
															: [];
														handleMetadataChange(key, values);
													} else {
														// Single select
														const value = getOptionValue(option);
														handleMetadataChange(key, value);
													}
													setMetadataInputValues((prev) => ({
														...prev,
														[key]: "",
													}));
												}}
												value={getMetadataValue(key, meta)}
												isCreatable={true}
												onInputChange={(inputValue, action) => {
													if (action.action === "input-change") {
														setMetadataInputValues((prev) => ({
															...prev,
															[key]: inputValue,
														}));
													}
												}}
												onBlur={() => {
													const inputValue = metadataInputValues[key];
													const options = meta.values.map((val) => val);
													if (inputValue && !options.includes(inputValue)) {
														if (!!meta.allowMultiple) {
															const prev = formData.metadata?.[key] || [];
															const newArr = Array.isArray(prev)
																? [...prev, inputValue]
																: [inputValue];
															handleMetadataChange(key, newArr);
														} else {
															handleMetadataChange(key, inputValue);
														}
														setMetadataInputValues((prev) => ({
															...prev,
															[key]: "",
														}));
													}
												}}
											/>
										)}
									</FaiBox>
								</Grid>
							);
						}
					)}
				</Grid>
			</FaiBox>
		);
	};

	const onFileChanged = (file) => {
		setFormData((prev) => ({ ...prev, sizeChartMedia: file?.[0] }));
	};

	const handleShippingEssentialsChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const { name, value } = e.target;
		// Keep the raw string while the field is empty or mid-typing ("0.",
		// "."), otherwise parseFloat collapses it and the caret jumps — which
		// made it impossible to type a leading-zero decimal like 0.4.
		// `parseFloat("") || 0` also silently wrote 0, and a 0 kg product makes
		// Shiprocket fall back to its minimum weight tier.
		const parsed =
			value === "" ? "" : Number.isNaN(parseFloat(value)) ? "" : parseFloat(value);
		setFormData((prev: any) => ({
			...prev,
			shippingEssentials: {
				...prev.shippingEssentials,
				[name]: value.endsWith(".") || value === "0." ? value : parsed,
			},
		}));
	};

	const handleDeleteSizeChart = async () => {
		if (!formData.id) {
			toast.error("Product ID not found. Cannot delete size chart media.");
			return;
		}
		try {
			await ProductController.deleteSizeChart(formData.id);
			setMedia((prev) => ({ ...prev, sizeChartMedia: null }));
		} catch (error) {
			toast.error("Failed to delete size chart media.");
		}
	};

	// Accessories (e.g. ties, belts) have no sizing, so the size chart is neither
	// shown nor required for them. Detected off the selected MASTER category name
	// ("Accessories"), which is where all accessory products live.
	const selectedMaster = masterCategories.find(
		(m) => m.id === (selectedCategory || formData.masterCategoryId)
	);
	const isAccessoryCategory = /accessor/i.test(selectedMaster?.name || "");

	return (
		<>
			<FaiBox>
				<form>
					<FaiBox className={"details"}>
						<FaiTypography
							variant={"h5"}
							fontSize={typography.size.lg}
							fontWeight={typography.fontWeightMedium}
						>
							Product Name and ID
						</FaiTypography>
						<FaiBox mb={2}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FaiBox mb={3}>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Brand Name
											</FaiTypography>
										</FaiBox>
										<FaiSelect
											placeholder={"Select Brand"}
											name={"brandName"}
											options={brandOptions}
											onChange={handleBrandChange}
											value={getSelectedOption(
												formData.brandName,
												brandOptions
											)}
											isLoading={loading.brand}
										/>
									</FaiBox>
								</Grid>
								<Grid item md={12} lg={6}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Choose Category
											</FaiTypography>
										</FaiBox>
										<FaiSelect
											placeholder={"Choose Category"}
											name={"masterCategoryId"}
											options={masterCategories.map((category) => ({
												value: category.id,
												label: category.name,
											}))}
											onChange={handleCategoryChange}
											value={getSelectedOption(
												formData.masterCategoryId,
												masterCategories.map((category) => ({
													value: category.id,
													label: category.name,
												}))
											)}
											isLoading={loading.categories}
										/>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormField
										label={"Product Name"}
										name={"productName"}
										placeholder={"Enter product name"}
										onChange={handleInputChange}
										value={formData.productName || ""}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormField
										label={"SKU Code"}
										name={"skuCode"}
										placeholder={"Enter SKU code"}
										onChange={handleInputChange}
										value={formData.skuCode || ""}
									/>
								</Grid>
								<Grid item md={12} lg={6}>
									<FaiBox mb={1}>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Choose Sub Category
											</FaiTypography>
										</FaiBox>
										<FaiSelect
											placeholder={"Choose Product Type"}
											name={"categoryId"}
											options={productTypes.map((type) => ({
												value: type.id,
												label: type.name,
											}))}
											onChange={handleProductTypeChange}
											value={getSelectedOption(
												formData.categoryId,
												productTypes.map((type) => ({
													value: type.id,
													label: type.name,
												}))
											)}
											isLoading={loading.productTypes}
											isDisabled={!selectedCategory}
										/>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormField
										label={"Manufacturer"}
										name={"manufacturer"}
										placeholder={"Enter manufacturer name"}
										onChange={handleInputChange}
										value={formData.manufacturer || ""}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormField
										label={"Article Number"}
										name={"articleNumber"}
										placeholder={"Enter article number"}
										onChange={handleInputChange}
										value={formData.articleNumber || ""}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
										<FaiTypography
											component="label"
											variant="caption"
											fontWeight="bold"
											textTransform="capitalize"
										>
											Return
										</FaiTypography>
									</FaiBox>
									<FaiSelect
										placeholder="Select Return Option"
										name="isReturnable"
										options={[
											{ value: "true", label: "Yes" },
											{ value: "false", label: "No" },
										]}
										onChange={handleReturnableChange}
										value={getSelectedOption(
											formData.isReturnable?.toString() || "false",
											[
												{ value: "true", label: "Yes" },
												{ value: "false", label: "No" },
											]
										)}
									/>
								</Grid>
								{/* Size chart is ALWAYS shown. It is REQUIRED for every
								    category except Accessories (ties, belts, etc.), which
								    have no sizing and where it stays optional. */}
								<Grid item xs={12}>
									<FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
										<FaiTypography
											component="label"
											variant="caption"
											fontWeight="bold"
											textTransform="capitalize"
										>
											Chart Media Upload
											{isAccessoryCategory ? " (optional)" : " *"}
										</FaiTypography>
									</FaiBox>
									<DropzoneArea
										onChange={(files) => onFileChanged(files)}
										dropzoneText="Drag your file(s) or browse"
										filesLimit={1}
									/>
									{isEditMode && media.sizeChartMedia && (
										<FaiBox display="flex" alignItems="center">
											<img
												src={media.sizeChartMedia}
												alt=""
												className="h-32 w-32 m-2 border"
											/>
											<FaiBox ml={1}>
												<DeleteIcon
													fontSize="medium"
													style={{ cursor: "pointer", color: "red" }}
													onClick={handleDeleteSizeChart}
												/>
											</FaiBox>
										</FaiBox>
									)}
								</Grid>

								<Grid item xs={12}>
									<FaiBox mb={1} mt={3}>
										<FaiTypography
											variant={"h5"}
											fontSize={typography.size.lg}
											fontWeight={typography.fontWeightMedium}
										>
											Shipping Essentials
										</FaiTypography>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Length (cm)
											</FaiTypography>
										</FaiBox>
										{/* type="number" defaults to step="1", which makes the
										    browser reject decimals like 0.4. Backend stores Float. */}
										<FaiInput
											type="number"
											name="length"
											inputProps={{ step: "0.1", min: "0" }}
											value={formData.shippingEssentials?.length ?? ""}
											onChange={handleShippingEssentialsChange}
											placeholder="e.g. 30"
										/>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Breadth (cm)
											</FaiTypography>
										</FaiBox>
										{/* type="number" defaults to step="1", which makes the
										    browser reject decimals like 0.4. Backend stores Float. */}
										<FaiInput
											type="number"
											name="breadth"
											inputProps={{ step: "0.1", min: "0" }}
											value={formData.shippingEssentials?.breadth ?? ""}
											onChange={handleShippingEssentialsChange}
											placeholder="e.g. 22"
										/>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Height (cm)
											</FaiTypography>
										</FaiBox>
										{/* type="number" defaults to step="1", which makes the
										    browser reject decimals like 0.4. Backend stores Float. */}
										<FaiInput
											type="number"
											name="height"
											inputProps={{ step: "0.1", min: "0" }}
											value={formData.shippingEssentials?.height ?? ""}
											onChange={handleShippingEssentialsChange}
											placeholder="e.g. 4"
										/>
									</FaiBox>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FaiBox>
										<FaiBox
											mb={1}
											ml={0.5}
											lineHeight={0}
											display="inline-block"
										>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Weight (kg)
											</FaiTypography>
										</FaiBox>
										{/* type="number" defaults to step="1", which makes the
										    browser reject decimals like 0.4. Backend stores Float. */}
										<FaiInput
											type="number"
											name="weight"
											inputProps={{ step: "0.01", min: "0" }}
											value={formData.shippingEssentials?.weight ?? ""}
											onChange={handleShippingEssentialsChange}
											placeholder="e.g. 0.35"
										/>
									</FaiBox>
								</Grid>
							</Grid>
						</FaiBox>

						{selectedProductType && renderMetadataFields()}

						<FaiBox className={"variants"} mb={2} mt={2}>
							<FaiTypography
								variant={"h5"}
								fontSize={typography.size.lg}
								fontWeight={typography.fontWeightMedium}
							>
								Product Description*
							</FaiTypography>
							<Grid container spacing={2}>
								<Grid item md={12} lg={12}>
									<FaiBox mb={4}>
										<FormField
											multiline
											rows={5}
											label={"Product Details"}
											name={"description"}
											placeholder={"Description"}
											onChange={handleInputChange}
											value={formData.description || ""}
										/>
									</FaiBox>
								</Grid>
							</Grid>
						</FaiBox>
					</FaiBox>
				</form>
			</FaiBox>
		</>
	);
}

export default DetailForm;
